
/**
 * This store used to maintain session data, navigation url and search result params
 * into vuex store. These details used to maintain search result for recent search.
 * sessionData used to get session_id which we are passing in each formPost API
 */
/* eslint no-else-return: */
const state = {
  sessionData: null,
  searchResultParams: null,
  navUrl: null,
  forceBindStatus: false,
  baldwinEpicQueryParams: null,
};

/* eslint no-shadow: ["error", { "allow": ["state"] }] */
const getters = {
  getBaldwinEpicQueryParams(state) {
    return state.baldwinEpicQueryParams;
  },
  getForceBindStatus(state) {
    return state.forceBindStatus;
  },
  /**
   * Getter used to return current navigation API which will be called.
   * @param {*} state vuex state
   */
  getNavLink(state) {
    const TOUCHED_ENDPOINT_KEY = 'touched_endpoints'; // TODO: Use config value
    const FALLBACK_ENDPOINT = '/error'; // TODO: Use config value
    // TODO: Pull the session's current state directly from 1) the session ID and 2) the quote ID.
    /* eslint one-var:  */
    let endpoint = state.navUrl || sessionStorage.getItem('navUrl') || FALLBACK_ENDPOINT;
    const touchedEndpoints = JSON.parse(sessionStorage.getItem(TOUCHED_ENDPOINT_KEY)) || [];

    // TODO: URIs should be treated as exact but the API varies its endpoints' case
    endpoint = endpoint.toLowerCase();

    /**
     * Search the list of touched endpoints from the session storage.
     * If this endpoint has not been touch, add it to the list and
     * return the updated list to session storage.
     */
    if (touchedEndpoints.indexOf(endpoint) === -1) {
      touchedEndpoints.push(endpoint);
      sessionStorage.setItem(TOUCHED_ENDPOINT_KEY, JSON.stringify(touchedEndpoints));
    }

    return endpoint;
  },
  /**
   * This getter used to return session id from vuex store. If its not available in store then
   * it will get returned from browser session store.
   * This getter called from all places where formPost has been made
   * @param {*} state vuex state
   */
  getSessionID(state) {
    if (!state.sessionData) {
      const sessData = JSON.parse(sessionStorage.getItem('sessionData'));
      if (sessData && sessData.SessionId) {
        return sessData.SessionId;
      } else {
        return false;
      }
    } else {
      return state.sessionData.SessionId;
    }
  },
  /**
   * This getter used to return last search request parameter and used to load last search result
   * This getter get called form Search.Vue component
   * @param {*} state vuex state
   */
  getSearchParams(state) {
    if (!state.searchResultParams) {
      return JSON.parse(sessionStorage.getItem('searchDataParams'));
    } else {
      return state.searchResultParams;
    }
  },
};

const mutations = {
  forceBindSuccessful(state, status) {
    state.forceBindStatus = status;
  },
  /**
   * This mutation set selected quote data from search result into vuex store
   * @param {*} state vuex state
   * @param {*} resSessionData select quote data from search result
   */
  setSessionData(state, resSessionData) {
    state.sessionData = resSessionData;
  },
  /**
   * This mutation set CRM quote data from CRM to store
   * @param {*} state vuex state
   * @param {*} resSessionData select quote data from search result
   */
  setSessionDataCRM(state, resSessionData) {
    state.sessionData = resSessionData;
  },
  /**
   * This mutation unset session data from store and browser session storage.
   * usually on action like logout
   * @param {*} state  vuex state
   */
  clearSessionData(state) {
    state.sessionData = null;
    state.searchResultParams = null;
    sessionStorage.setItem('searchDataParams', null);
  },
  /**
   * Mutation used to set recent search query parameter in vuex store
   * @param {*} state vuex state
   * @param {*} searchResultParams search query parameters
   */
  setSearchDataParams(state, searchResultParams) {
    /* Set search result into state to retrieved it for search result link */
    state.searchResultParams = searchResultParams;
  },
  /**
   * Set current navigation link into vuex store returned from search result and all
   * other places where API returned response as a navigation api
   * @param {*} state vuex state
   * @param {*} linkData navigation url
   */
  setNavLink(state, linkData) {
    /* set navigation url to be called */
    state.navUrl = linkData;
  },
  setBaldwinEpicQueryParams(state, queryParams) {
    /* set query params received from Baldwin Epic link */
    state.baldwinEpicQueryParams = queryParams;
  },
};

const actions = {
  setForceBindSuccessful({ commit }, status) {
    commit('forceBindSuccessful', status);
  },
  /**
   * This action set current navigation url by calling respective mutation
   * Action get dispatched from all places where API response has navigation url only
   * @param {*} link navigation link from API response
   */
  setCurrentNav({ commit }, link) {
    sessionStorage.setItem('navUrl', link);
    commit('setNavLink', link);
  },
  /**
   * This action call mutation to set search query params of recent search.
   * Action get called from Search.Vue component
   * @param {*} formData search query params
   */
  setSearchResultParams({ commit }, formData) {
    sessionStorage.setItem('searchDataParams', JSON.stringify(formData));
    commit('setSearchDataParams', formData);
  },
  /**
   * This action gets called on quote creation from search component.
   * It used to set session data and navigation link in vuex store.
   * @param {*} response response from create quote API
   */
  createQuote({ commit }, response) {
    commit('setSessionData', response.data);
    sessionStorage.setItem('sessionData', JSON.stringify(response.data));
    commit('setNavLink', response.data.Link);
  },
  /**
   * set session data into vuex store from search component
   * @param {*} data response from selected quote Link url API
   */
  setSessionQuote({ commit }, data) {
    sessionStorage.setItem('sessionData', JSON.stringify(data));
    commit('setSessionData', data);
  },
  /**
   * set session data null into vuex store for crm import
   * @param {*} data response from selected quote Link url API
   */
  setSessionQuoteCRM({ commit }, data) {
    sessionStorage.setItem('sessionData', JSON.stringify(data));
    commit('setSessionDataCRM', data);
  },
  /**
   * set session data for Baldwin Epic query params from epic links
   * @param {*} data object with properties:
   * {
   *   area: string,
   *   user: string,
   *   ClientID: string,
   *   EntityID: string,
   *   a: string,
   * },
   */
  setBaldwinEpicQueryParams({ commit }, data) {
    commit('setBaldwinEpicQueryParams', data);
  },
};

/**
 * export module to main store.js
 */
export default {
  state,
  mutations,
  actions,
  getters,
};



// WEBPACK FOOTER //
// ./src/store/modules/search.js